import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import {
  AGE_GROUPS,
  ISSUE_GROUP_SERVER_KEY,
  UNASSIGNED_DIMENSION_LABEL,
  UNASSIGNED_GROUP_KEY,
  buildGroupWhere,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useIssueGroupCounts } from "./use-issue-group-counts";
import type { Omittable } from "@salestrak/ui";

/** One grouped-table group: its key/label, exact server total, and folded `where`. */
export type IssueQueryGroup = {
  key: string;
  label: string;
  totalCount: number;
  where: Record<string, unknown> | undefined;
};

/**
 * The group SET for a grouped, server-paged issue table. The exact server
 * `issueGroupCounts` aggregation over the caller's folded `where` names the
 * non-empty groups — never a loaded sample — and each group's `where` folds
 * the base predicate with its dimension selector, ready for one windowed
 * query per group. Groups order by the dimension's own display order (stage
 * rail order, age bucket order, label A–Z otherwise) with the unassigned
 * bucket last. `groups` is null while `dimension` is null (ungrouped), and
 * `[]` until the aggregation is ready.
 */
export function useIssueQueryGroups({
  where,
  dimension,
}: {
  where: Record<string, unknown> | undefined;
  dimension: IssueGroupDimension | null;
}): Omittable<{
  groups: IssueQueryGroup[] | null;
  /** True once the aggregation answered (always true while ungrouped). */
  ready: boolean;
  error?: ErrorLike;
}> {
  const { stages, stageMap, workflowMap, issueTypeMap } = useWorkspaceData();
  const counts = useIssueGroupCounts({
    where: dimension ? (where ?? {}) : undefined,
    groupBy: dimension ? ISSUE_GROUP_SERVER_KEY[dimension] : undefined,
  });

  const groups = useMemo<IssueQueryGroup[] | null>(() => {
    if (!dimension) return null;
    if (!counts.ready) return [];
    const stageOrder = new Map(stages.map((s, i) => [s.stageId, i]));
    const ageOrder = new Map(AGE_GROUPS.map((g, i) => [g.key, i]));
    const labelOf = (key: string): string => {
      if (key === UNASSIGNED_GROUP_KEY) return UNASSIGNED_DIMENSION_LABEL[dimension];
      switch (dimension) {
        case "stage":
          return stageMap.get(key)?.name ?? key;
        case "workflow":
          return workflowMap.get(key)?.name ?? key;
        case "age":
          return AGE_GROUPS.find((g) => g.key === key)?.label ?? key;
        case "issueType":
          return issueTypeMap.get(key)?.name ?? key;
        default:
          return key;
      }
    };
    const defs = Object.entries(counts.counts)
      .filter(([, total]) => total > 0)
      .map(([key, total]) => ({
        key,
        label: labelOf(key),
        totalCount: total,
        where: combineWhereConditions(
          [where, buildGroupWhere(dimension, key)].filter(
            (c): c is Record<string, unknown> => c != null,
          ),
        ),
      }));
    defs.sort((a, b) => {
      const unassigned =
        Number(a.key === UNASSIGNED_GROUP_KEY) - Number(b.key === UNASSIGNED_GROUP_KEY);
      if (unassigned !== 0) return unassigned;
      if (dimension === "stage") {
        return (
          (stageOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (stageOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER)
        );
      }
      if (dimension === "age") {
        return (
          (ageOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (ageOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER)
        );
      }
      return a.label.localeCompare(b.label);
    });
    return defs;
  }, [dimension, counts.ready, counts.counts, stages, stageMap, workflowMap, issueTypeMap, where]);

  return { groups, ready: !dimension || counts.ready, error: counts.error };
}
